/**
 * 底图样式。
 *
 * ★ 两个 app 都要画地图,这段逻辑只能有一份。
 *   之前是各自一个 `mapStyle.ts`,内容一模一样 ——
 *   换供应商要改两处、而且总有一处会被忘掉。
 *   这里只做纯计算,读环境变量由各自的 app 负责(Vite 的
 *   `import.meta.env` 在共享包里拿不到)。
 */

/** OSM 的公共瓦片服务。**只能开发用** —— 它的使用政策明确禁止商业/高流量 */
export const DEV_ONLY_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export interface MapConfig {
  /** 栅格瓦片地址,或矢量样式的 style.json 地址 */
  tileUrl: string;
  attribution: string;
  /** 供应商的 key。用 `{key}` 占位符写在地址里 */
  apiKey?: string;
  /** 生产构建?决定「没配底图」是警告还是拦下 */
  isProd: boolean;
}

export interface ResolvedMap {
  /** 最终地址,占位符已替换 */
  url: string;
  attribution: string;
  /** 是矢量样式(style.json)还是栅格瓦片 */
  kind: 'style' | 'raster';
  /** 用的是开发用的公共瓦片吗 —— 界面上要标出来 */
  isDevTiles: boolean;
  /** 有问题时给一句人话。null 表示配置没问题 */
  problem: string | null;
}

/**
 * 判断配置好了没有。
 *
 * ★ 「没配」和「配了但还是 OSM」要分开说。
 *   前者是忘了配,后者是有人真的把 OSM 填进去了 ——
 *   后一种更值得警告,因为他以为自己配好了。
 */
export function resolveMap(cfg: MapConfig): ResolvedMap {
  const raw = cfg.tileUrl?.trim() || DEV_ONLY_TILES;
  const isDevTiles = raw.includes('tile.openstreetmap.org');

  const url = cfg.apiKey ? raw.replace(/\{key\}/g, cfg.apiKey) : raw;
  // style.json 是矢量样式,直接交给 MapLibre;别的当栅格瓦片模板
  const kind: 'style' | 'raster' = /\.json(\?|$)/.test(raw) ? 'style' : 'raster';

  /*
   * ★ 提示里要说**去哪配**,而且说的得是现在真正生效的那个地方。
   *
   *   这句话原来写的是「设 VITE_MAP_TILE_URL」—— 那是构建期变量,
   *   自从底图改成后台可配之后,照着它做的人会去改 .env 然后重启,
   *   发现提示还在,以为是没生效。而正确的地方是「设置 → 地图底图」。
   *
   *   一句指错地方的提示比没有提示更费时间:它让人有事可做,
   *   而做的那件事不起作用。
   */
  let problem: string | null = null;
  if (isDevTiles) {
    problem = cfg.isProd
      ? '正在用 OpenStreetMap 的公共瓦片 —— 它的使用政策禁止商业/高流量使用,' +
        '上线前必须在「设置 → 地图底图」里换成 MapTiler / Thunderforest / Mapbox 或自建服务'
      : '开发用底图(OSM 公共瓦片),只能开发用。生产前在「设置 → 地图底图」里换掉 ——' +
        '注意它和地理编码是两回事,配了 Google 的 key 也不顶用';
  } else if (raw.includes('{key}') && !cfg.apiKey) {
    // 地址里留着占位符却没给 key —— 请求会 401,而地图只是白屏
    problem =
      '底图地址里有 {key} 占位符,但取不到密钥 —— 瓦片会请求失败,地图一片空白。' +
      '在「设置 → 地图底图」里填密钥引用(env:变量名),并确认服务器上有那个环境变量';
  }

  return { url, attribution: cfg.attribution?.trim() || '© OpenStreetMap contributors', kind, isDevTiles, problem };
}

/** MapLibre 的栅格样式。夜间把底图压暗,而不是另换一套瓦片 —— 夜里送货是刚需 */
export function rasterStyle(url: string, attribution: string, dark: boolean): unknown {
  return {
    version: 8,
    sources: {
      base: { type: 'raster', tiles: [url], tileSize: 256, attribution, maxzoom: 19 },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': dark ? '#0f172a' : '#e2e8f0' } },
      {
        id: 'base',
        type: 'raster',
        source: 'base',
        paint: {
          'raster-brightness-max': dark ? 0.6 : 1,
          'raster-saturation': dark ? -0.3 : 0,
          'raster-opacity': 1,
        },
      },
    ],
  };
}
